import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getIssues,
  createIssue,
  getSubmissions,
  preparePdfs,
  getArticlePdfBlob,
  fetchFileBlob,
  publishToSite,
  assignDoi,
  updateZenodoPdf,
  batchAssignDoi,
  getCertificatesZip,
} from '../api/client';
import { useAuth } from '../App';

/* --- tiny helpers ------------------------------------------------ */
const Spinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
  </div>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const getArticleTimestamp = (submission) => {
  const date =
    submission?.accepted_at ||
    submission?.updated_at ||
    submission?.submitted_at ||
    submission?.created_at;
  const ts = date ? new Date(date).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
};

/* ----------------------------------------------------------------- */
export default function LatexPage() {
  const { selectedJournalSlug, currentJournal } = useAuth();
  /* state */
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  /* selected issue */
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [subLoading, setSubLoading] = useState(false);

  /* sections */
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);

  /* articles for selected section (reorderable) */
  const [sectionArticles, setSectionArticles] = useState([]);

  /* building PDF */
  const [building, setBuilding] = useState(false);

  /* PDF results after prepare */
  const [combinedPdfName, setCombinedPdfName] = useState(null);
  const [articlePdfs, setArticlePdfs] = useState({}); // { submissionId: pdfPath }
  const [previewUrl, setPreviewUrl] = useState(null); // currently previewed PDF url

  /* publishing */
  const [publishing, setPublishing] = useState(false);

  /* DOI assignment */
  const [doiLoading, setDoiLoading] = useState({});

  /* create issue modal */
  const [createOpen, setCreateOpen] = useState(false);
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newNumber, setNewNumber] = useState('1');
  const newVolume = String(parseInt(newYear) - 2025);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /* --- loaders --------------------------------------------------- */
  const loadIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIssues();
      setIssues(res.data || []);
    } catch (err) {
      toast.error('Sonlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  }, [selectedJournalSlug]);

  useEffect(() => {
    setSelectedIssue(null);
    setSelectedSection(null);
    setSectionArticles([]);
    setCombinedPdfName(null);
    setArticlePdfs({});
    setPreviewUrl(null);
    loadIssues();
  }, [loadIssues]);

  /* select issue -> load published submissions -> extract sections */
  const selectIssue = useCallback(async (issue) => {
    setSelectedIssue(issue);
    setSelectedSection(null);
    setSectionArticles([]);
    setSections([]);
    setCombinedPdfName(null);
    setArticlePdfs({});
    setPreviewUrl(null);
    setSubLoading(true);
    try {
      const res = await getSubmissions({ issue_id: issue.id, status: 'published' });
      const subs = (res.data || [])
        .slice()
        .sort((a, b) => {
          // Keep chronological order so newly accepted articles appear at the bottom.
          const diff = getArticleTimestamp(a) - getArticleTimestamp(b);
          if (diff !== 0) return diff;
          return (a.id || 0) - (b.id || 0);
        });
      setAllSubmissions(subs);

      /* extract unique sections */
      const uniqueSections = [...new Set(subs.map(s => s.section || 'Boshqa'))];
      setSections(uniqueSections);
    } catch (err) {
      toast.error('Maqolalarni yuklashda xatolik');
      setAllSubmissions([]);
      setSections([]);
    } finally {
      setSubLoading(false);
    }
  }, []);

  /* select section -> filter articles, detect existing PDFs */
  const selectSection = useCallback((section) => {
    setSelectedSection(section);
    setCombinedPdfName(null);
    setPreviewUrl(null);
    const filtered = allSubmissions.filter(s => (s.section || 'Boshqa') === section);
    setSectionArticles([...filtered]);

    /* detect articles that already have PDFs */
    const existing = {};
    filtered.forEach(s => {
      if (s.article_pdf_path) existing[s.id] = s.article_pdf_path;
    });
    setArticlePdfs(existing);
  }, [allSubmissions]);

  /* --- reorder --------------------------------------------------- */
  const moveArticle = (index, direction) => {
    const arr = [...sectionArticles];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setSectionArticles(arr);
  };

  /* --- create issue ---------------------------------------------- */
  const handleCreate = async () => {
    setCreating(true);
    const title = `${newYear}-yil ${newNumber}-son`;
    try {
      await createIssue({
        title,
        volume: newVolume,
        number: newNumber,
        year: parseInt(newYear),
        is_open: false,
      });
      toast.success(`${title} yaratildi`);
      setCreateOpen(false);
      loadIssues();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    } finally {
      setCreating(false);
    }
  };

  /* --- prepare PDFs (combined + individual) ----------------------- */
  const handlePreparePdfs = async () => {
    if (sectionArticles.length === 0) {
      toast.error('Maqolalar topilmadi');
      return;
    }

    setBuilding(true);
    try {
      const res = await preparePdfs({
        issue_id: selectedIssue.id,
        section: selectedSection || '',
        submission_ids: sectionArticles.map(a => a.id),
      });

      const data = res.data;
      setCombinedPdfName(data.combined_pdf);

      /* store per-article PDF paths */
      const pdfs = {};
      const pageRanges = {};
      (data.articles || []).forEach(a => {
        pdfs[a.id] = a.pdf_path;
        pageRanges[a.id] = {
          first_page: a.first_page ?? null,
          last_page: a.last_page ?? null,
        };
      });
      setArticlePdfs(pdfs);

      /* show combined PDF preview */
      try {
        const previewRes = await fetchFileBlob(data.combined_pdf, true);
        const objectUrl = URL.createObjectURL(previewRes.data);
        setPreviewUrl((prev) => {
          if (prev?.startsWith('blob:')) {
            URL.revokeObjectURL(prev);
          }
          return objectUrl;
        });
      } catch (err) {
        setPreviewUrl(null);
        toast.error(err.response?.data?.detail || 'Yig‘ma PDF preview yuklanmadi');
      }

      /* update local allSubmissions so section re-select keeps state */
      setAllSubmissions(prev => prev.map(s =>
        pdfs[s.id]
          ? {
              ...s,
              article_pdf_path: pdfs[s.id],
              first_page: pageRanges[s.id]?.first_page ?? s.first_page,
              last_page: pageRanges[s.id]?.last_page ?? s.last_page,
            }
          : s
      ));
      setSectionArticles(prev => prev.map(s =>
        pdfs[s.id]
          ? {
              ...s,
              article_pdf_path: pdfs[s.id],
              first_page: pageRanges[s.id]?.first_page ?? s.first_page,
              last_page: pageRanges[s.id]?.last_page ?? s.last_page,
            }
          : s
      ));

      toast.success(`${data.articles.length} ta PDF muvaffaqiyatli yaratildi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF yaratishda xatolik');
    } finally {
      setBuilding(false);
    }
  };

  /* --- publish to site ------------------------------------------- */
  const handlePublish = async () => {
    if (!selectedIssue) return;
    setPublishing(true);
    try {
      const res = await publishToSite({ issue_id: selectedIssue.id });
      toast.success(res.data.message || 'Saytda chop etildi!');
      loadIssues(); // refresh issue list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chop etishda xatolik');
    } finally {
      setPublishing(false);
    }
  };

  /* --- preview a single article PDF (open in new tab) ------------ */
  const handleViewArticlePdf = async (submissionId) => {
    try {
      const res = await getArticlePdfBlob(submissionId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        URL.revokeObjectURL(objectUrl);
        toast.error('PDF oynasini ochish bloklandi. Iltimos, popup ruxsatini yoqing.');
        return;
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDFni ochishda xatolik');
    }
  };

  /* --- download per-author certificates ZIP ----------------------- */
  const handleDownloadCertificates = async (sub) => {
    const kind = sub.status === 'published' ? 'published' : 'accepted';
    try {
      const res = await getCertificatesZip(sub.id, kind);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Certificates_Sub${sub.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      toast.success('Sertifikatlar yuklab olindi');
    } catch (err) {
      let detail = err.response?.data?.detail;
      // Blob responseType means error body is also a blob — try to read it
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || detail;
        } catch (_) {
          /* ignore */
        }
      }
      toast.error(detail || 'Sertifikat yuklab olishda xatolik');
    }
  };

  /* --- preview combined PDF (open in new tab) -------------------- */
  const handleViewCombinedPdf = async () => {
    if (!combinedPdfName) return;
    try {
      const res = await fetchFileBlob(combinedPdfName, true);
      const objectUrl = URL.createObjectURL(res.data);
      const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        URL.revokeObjectURL(objectUrl);
        toast.error('PDF oynasini ochish bloklandi. Iltimos, popup ruxsatini yoqing.');
        return;
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yig‘ma PDFni ochishda xatolik');
    }
  };

  /* --- assign DOI to a single article ----------------------------- */
  const handleAssignDoi = async (sub) => {
    if (!window.confirm(`"${sub.title}" maqolasiga DOI tayinlashni tasdiqlaysizmi?`)) return;
    setDoiLoading(prev => ({ ...prev, [sub.id]: true }));
    try {
      const res = await assignDoi(sub.id);
      toast.success(`DOI tayinlandi: ${res.data?.doi || 'OK'}`);
      // Update local state
      setAllSubmissions(prev => prev.map(s =>
        s.id === sub.id ? { ...s, doi: res.data?.doi, doi_assigned_at: new Date().toISOString() } : s
      ));
      setSectionArticles(prev => prev.map(s =>
        s.id === sub.id ? { ...s, doi: res.data?.doi, doi_assigned_at: new Date().toISOString() } : s
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'DOI tayinlashda xatolik');
    } finally {
      setDoiLoading(prev => ({ ...prev, [sub.id]: false }));
    }
  };

  /* --- update Zenodo PDF ------------------------------------------ */
  const handleUpdateZenodoPdf = async (sub) => {
    setDoiLoading(prev => ({ ...prev, [sub.id]: true }));
    try {
      await updateZenodoPdf(sub.id);
      toast.success('Zenodo PDF yangilandi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    } finally {
      setDoiLoading(prev => ({ ...prev, [sub.id]: false }));
    }
  };

  /* --- batch assign DOI to all articles without DOI --------------- */
  const [batchDoiLoading, setBatchDoiLoading] = useState(false);

  const handleBatchAssignDoi = async () => {
    const noDoi = allSubmissions.filter(s => !s.doi && s.article_pdf_path && s.status === 'published');
    if (noDoi.length === 0) {
      toast('Barcha maqolalarda DOI mavjud yoki PDF yaratilmagan');
      return;
    }
    if (!window.confirm(`${noDoi.length} ta maqolaga DOI tayinlashni tasdiqlaysizmi?`)) return;
    setBatchDoiLoading(true);
    try {
      const res = await batchAssignDoi(noDoi.map(s => s.id));
      const data = res.data;
      toast.success(data.message || `${(data.assigned || []).length} ta DOI tayinlandi`);
      // Update local state with assigned DOIs
      const assignedMap = {};
      (data.assigned || []).forEach(a => { assignedMap[a.id] = a.doi; });
      setAllSubmissions(prev => prev.map(s =>
        assignedMap[s.id] ? { ...s, doi: assignedMap[s.id], doi_assigned_at: new Date().toISOString() } : s
      ));
      setSectionArticles(prev => prev.map(s =>
        assignedMap[s.id] ? { ...s, doi: assignedMap[s.id], doi_assigned_at: new Date().toISOString() } : s
      ));
      // Show failures in toast
      if ((data.failed || []).length > 0) {
        data.failed.forEach(f => {
          const category = f.category ? ` [${f.category}]` : '';
          toast.error(`#${f.id}${category}: ${f.error}`);
        });
      }
      if ((data.skipped || []).length > 0) {
        data.skipped
          .filter(s => s.category && s.category !== 'RULE')
          .forEach(s => toast.error(`#${s.id} [${s.category}]: ${s.reason}`));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch DOI tayinlashda xatolik');
    } finally {
      setBatchDoiLoading(false);
    }
  };

  const allHavePdfs = sectionArticles.length > 0 &&
    sectionArticles.every(s => articlePdfs[s.id]);

  /* --- render ---------------------------------------------------- */
  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">LaTeX / PDF</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {(currentJournal?.short_name || currentJournal?.name || 'Tanlangan jurnal')} uchun PDF tayyorlash
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* -- column 1: issues ----------------------------------- */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">Sonlar</h3>
              <button
                onClick={() => setCreateOpen(true)}
                className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"
                title="Yangi son"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </button>
            </div>
            <div className="divide-y divide-slate-50 max-h-[70vh] overflow-y-auto">
              {issues.length === 0 && (
                <p className="text-center text-slate-400 text-sm py-10">Sonlar topilmadi</p>
              )}
              {issues.map((iss) => {
                const isActive = selectedIssue?.id === iss.id;
                return (
                  <button
                    key={iss.id}
                    onClick={() => selectIssue(iss)}
                    className={`w-full text-left px-5 py-3 text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <span className="font-medium block">{iss.title}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-400">{iss.year} yil</span>
                      {iss.submission_count != null && (
                        <span className="text-xs text-slate-400">&bull; {iss.submission_count} maqola</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* -- column 2: sections --------------------------------- */}
        <div className="lg:col-span-3">
          {!selectedIssue ? (
            <div className="bg-white rounded-2xl border border-slate-200 flex items-center justify-center h-48">
              <p className="text-slate-400 text-sm">&larr; Sonni tanlang</p>
            </div>
          ) : subLoading ? (
            <Spinner />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-700">Yo'nalishlar</h3>
                <p className="text-xs text-slate-400 mt-0.5">{selectedIssue.title}</p>
              </div>
              <div className="divide-y divide-slate-50 max-h-[70vh] overflow-y-auto">
                {sections.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-10">
                    Chop etilgan maqolalar yo'q
                  </p>
                ) : (
                  sections.map((sec) => {
                    const isActive = selectedSection === sec;
                    const count = allSubmissions.filter(s => (s.section || 'Boshqa') === sec).length;
                    return (
                      <button
                        key={sec}
                        onClick={() => selectSection(sec)}
                        className={`w-full text-left px-5 py-3 text-sm transition-colors ${
                          isActive
                            ? 'bg-indigo-50 text-indigo-700 font-medium'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{sec}</span>
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                            isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {count}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* -- column 3: articles + PDF workflow ------------------- */}
        <div className="lg:col-span-6 space-y-4">
          {!selectedSection ? (
            <div className="bg-white rounded-2xl border border-slate-200 flex items-center justify-center h-48">
              <p className="text-slate-400 text-sm">
                {selectedIssue ? "\u2190 Yo'nalishni tanlang" : "Son va yo'nalishni tanlang"}
              </p>
            </div>
          ) : (
            <>
              {/* toolbar */}
              <div className="bg-white dark:bg-[#161b22] rounded-2xl border border-slate-200 dark:border-[#30363d] px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-[#e6edf3]">
                    {selectedSection}
                  </h3>
                  <p className="text-xs text-slate-400 dark:text-[#6e7681] mt-0.5">
                    {sectionArticles.length} maqola &middot; {selectedIssue.title}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Prepare PDFs button */}
                  <button
                    onClick={handlePreparePdfs}
                    disabled={building || sectionArticles.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors"
                  >
                    {building ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Tayyorlanmoqda...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Maqolalarni tayyorlash
                      </>
                    )}
                  </button>

                  {/* Publish to site button — shown only when all articles have PDFs */}
                  {allHavePdfs && (
                    <button
                      onClick={handlePublish}
                      disabled={publishing}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      {publishing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          Chop etilmoqda...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Saytda chop etish
                        </>
                      )}
                    </button>
                  )}

                  {/* Batch DOI button — shown when there are articles without DOI */}
                  {allSubmissions.some(s => !s.doi && s.article_pdf_path && s.status === 'published') && (
                    <button
                      onClick={handleBatchAssignDoi}
                      disabled={batchDoiLoading}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      {batchDoiLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          DOI tayinlanmoqda...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Barchaga DOI
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* PDF quick actions */}
              {combinedPdfName && (
                <div className="bg-white rounded-2xl border border-slate-200 px-5 py-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">PDF ko'rish</h3>
                  <button
                    onClick={handleViewCombinedPdf}
                    className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Umumiy PDF
                  </button>
                </div>
              )}

              {/* articles list with reorder + PDF buttons */}
              {sectionArticles.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 flex items-center justify-center h-40">
                  <p className="text-slate-400 text-sm">Maqolalar topilmadi</p>
                </div>
              ) : (
                <div className="bg-white dark:bg-[#161b22] rounded-2xl border border-slate-200 dark:border-[#30363d] overflow-x-auto">
                  <table className="w-full text-sm min-w-[860px]">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-10">
                          №
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Maqola nomi
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Muallif
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">
                          Sana
                        </th>
                        <th className="text-center px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-16">
                          PDF
                        </th>
                        <th className="text-center px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-[150px] sticky right-24 z-20 bg-slate-50/95 dark:bg-[#1b2430]/95 backdrop-blur-sm border-l border-slate-100 dark:border-[#30363d]">
                          DOI
                        </th>
                        <th className="text-center px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider w-24 sticky right-0 z-20 bg-slate-50/95 dark:bg-[#1b2430]/95 backdrop-blur-sm border-l border-slate-100 dark:border-[#30363d]">
                          Tartib
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {sectionArticles.map((sub, idx) => (
                        <tr key={sub.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                            {idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-800 truncate max-w-[280px]">
                              {sub.title}
                              {sub.source_format === 'tex' && (
                                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">LaTeX</span>
                              )}
                            </p>
                            {sub.keywords && (
                              <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[280px]">
                                {sub.keywords}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-slate-600 text-xs">
                              {sub.author
                                ? `${sub.author.last_name} ${sub.author.first_name}`
                                : '\u2014'}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">
                            {formatDate(sub.accepted_at || sub.submitted_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {articlePdfs[sub.id] ? (
                              <div className="inline-flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleViewArticlePdf(sub.id)}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                  title="PDF ko'rish"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDownloadCertificates(sub)}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                                  title="Sertifikatlarni yuklab olish (ZIP)"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleDownloadCertificates(sub)}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                                title="Sertifikatlarni yuklab olish (ZIP)"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center sticky right-24 z-10 bg-white dark:bg-[#161b22] border-l border-slate-100 dark:border-[#30363d]">
                            {sub.doi ? (
                              <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700" title={sub.doi}>
                                  ✓ DOI
                                </span>
                                {articlePdfs[sub.id] && (
                                  <button
                                    onClick={() => handleUpdateZenodoPdf(sub)}
                                    disabled={doiLoading[sub.id]}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 whitespace-nowrap"
                                    title="Zenodo PDF yangilash"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                    Zenodo
                                  </button>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleAssignDoi(sub)}
                                disabled={doiLoading[sub.id] || sub.is_doi_pending}
                                className="inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                                title="DOI tayinlash"
                              >
                                {doiLoading[sub.id] || sub.is_doi_pending ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border border-indigo-600 border-t-transparent" />
                                ) : 'DOI'}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 sticky right-0 z-10 bg-white dark:bg-[#161b22] border-l border-slate-100 dark:border-[#30363d]">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => moveArticle(idx, -1)}
                                disabled={idx === 0}
                                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Yuqoriga"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => moveArticle(idx, 1)}
                                disabled={idx === sectionArticles.length - 1}
                                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Pastga"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* -- create issue modal ----------------------------------- */}
      {createOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setCreateOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-4">Yangi son yaratish</h3>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
                  Yil
                </label>
                <select
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {Array.from({ length: 6 }, (_, i) =>
                    String(new Date().getFullYear() - 1 + i)
                  ).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
                    Tom (avtomatik)
                  </label>
                  <input
                    value={`${newVolume}-tom`}
                    readOnly
                    className="w-full px-3 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm text-slate-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
                    Son
                  </label>
                  <select
                    value={newNumber}
                    onChange={(e) => setNewNumber(e.target.value)}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={String(n)}>
                        {n}-son
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCreateOpen(false)}
                className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm hover:bg-slate-50"
              >
                Bekor
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium"
              >
                {creating ? 'Yaratilmoqda...' : 'Yaratish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
